import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { signOut } from 'firebase/auth';
import { auth, db } from '../lib/firebase';
import { collection, onSnapshot, query, where, limit, getDocs, doc, setDoc, serverTimestamp } from 'firebase/firestore';

export default function MainLayout({
    children,
    currentView,
    onViewChange,
    dashboardTab,
    setDashboardTab,
    unviewedCount,
    agentCount
}) {
    const { user, profile } = useAuth();
    const scrollContainerRef = useRef(null);
    const [announcements, setAnnouncements] = useState([]);
    const [dismissedAnnouncements, setDismissedAnnouncements] = useState(() => {
        try { return JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]'); } catch { return []; }
    });

    useEffect(() => {
        if (!user) return undefined;
        const announcementsQuery = query(collection(db, 'announcements'), where('active', '==', true), limit(5));
        const stateQuery = collection(db, 'users', user.uid, 'announcementState');
        getDocs(stateQuery).then(snapshot => {
            setDismissedAnnouncements(current => [...new Set([...current, ...snapshot.docs.map(item => item.id)])]);
        }).catch(error => console.warn('[Announcements] State load failed:', error.message));
        return onSnapshot(announcementsQuery, snapshot => {
            setAnnouncements(snapshot.docs.map(item => ({ id: item.id, ...item.data() })).sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)));
        }, error => console.warn('[Announcements] Listener failed:', error.message));
    }, [user]);

    const visibleAnnouncements = announcements.filter(item => !dismissedAnnouncements.includes(item.id));
    const dismissAnnouncement = (id) => {
        setDismissedAnnouncements(current => {
            const next = [...new Set([...current, id])].slice(-50);
            localStorage.setItem('dismissedAnnouncements', JSON.stringify(next));
            if (user) {
                void setDoc(doc(db, 'users', user.uid, 'announcementState', id), {
                    dismissedAt: serverTimestamp()
                }, { merge: true });
            }
            return next;
        });
    };

    // Scroll to top when view or tab changes
    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo(0, 0);
        }
    }, [currentView, dashboardTab]);

    const handleSignOut = async () => {
        await signOut(auth);
    };

    const navItems = [
        {
            id: 'dashboard-analytics',
            label: 'Analytics',
            icon: '📈',
            view: 'dashboard',
            tab: 'analytics',
            active: currentView === 'dashboard' && dashboardTab === 'analytics'
        },
        {
            id: 'dashboard-sites',
            label: 'Sites',
            icon: '🌐',
            view: 'sites',
            active: currentView === 'sites'
        },
        {
            id: 'agents',
            label: 'Scanners',
            icon: '📡',
            view: 'agents',
            active: currentView === 'agents'
        },
        {
            id: 'dashboard-new',
            label: 'New Scan',
            icon: '⚡',
            view: 'dashboard',
            tab: 'new-scan',
            active: currentView === 'dashboard' && dashboardTab === 'new-scan'
        },
        {
            id: 'dashboard-history',
            label: 'Scans',
            icon: '📂',
            view: 'dashboard',
            tab: 'scans',
            active: currentView === 'dashboard' && dashboardTab === 'scans',
            badge: unviewedCount > 0 ? unviewedCount : null
        },
        {
            id: 'dashboard-pages',
            label: 'Pages',
            icon: '📄',
            view: 'dashboard',
            tab: 'pages',
            active: currentView === 'dashboard' && dashboardTab === 'pages'
        },
        {
            id: 'dashboard-issues',
            label: 'Issues',
            icon: '🚫',
            view: 'dashboard',
            tab: 'issues',
            active: currentView === 'dashboard' && dashboardTab === 'issues'
        },
        {
            id: 'dashboard-journeys',
            label: 'User Journeys',
            icon: '🛣️',
            view: 'dashboard',
            tab: 'journeys',
            active: currentView === 'dashboard' && dashboardTab === 'journeys',
        },
        {
            id: 'dashboard-reports',
            label: 'Reports',
            icon: '📊',
            view: 'dashboard',
            tab: 'reports',
            active: currentView === 'dashboard' && dashboardTab === 'reports',
        },
        {
            id: 'profile',
            label: 'Account',
            icon: '👤',
            view: 'profile',
            active: currentView === 'profile'
        },
    ];

    if (profile?.role === 'admin') {
        navItems.push({
            id: 'admin',
            label: 'Admin Hub',
            icon: '🛠️',
            view: 'admin',
            active: currentView === 'admin'
        });
    }

    return (
        <div className="flex h-screen bg-warm-pearl text-espresso overflow-hidden">
            {/* Sidebar */}
            <aside className="w-72 border-r border-espresso/10 bg-stone/30 flex flex-col z-50 print:hidden">
                <div className="p-8 pb-4">
                    <div className="flex items-center gap-4 mb-10 cursor-pointer" onClick={() => window.location.hash = 'home'}>
                        <img src="/logo.png" className="w-10 h-10 shadow-sm" alt="Accessibility Scout Logo" />
                        <div>
                            <h1 className="text-sm font-black uppercase tracking-tighter text-espresso">Accessibility Scout</h1>
                            <p className="text-[0.625rem] font-bold text-teal uppercase tracking-widest opacity-60">Control Center</p>
                        </div>
                    </div>

                    {/* Node Status Banner */}
                    <button
                        onClick={() => onViewChange('agents')}
                        className={`w-full flex items-center justify-between transition-all cursor-pointer border border-espresso/10 px-4 py-3 rounded-2xl mb-8 group ${agentCount > 0
                            ? 'bg-pass/5 text-pass border-pass/20'
                            : 'bg-fail/5 text-fail border-fail/20'
                            }`}
                    >
                        <div className="flex items-center gap-3">
                            <span className={`w-2 h-2 rounded-full ${agentCount > 0 ? 'bg-current animate-pulse' : 'bg-current opacity-50'}`} />
                            <span className="text-[0.625rem] font-black uppercase tracking-widest">
                                {agentCount > 0 ? 'Scanner Online' : 'Scanner Offline'}
                            </span>
                        </div>
                        <span className="text-[0.625rem] font-mono opacity-80">[{agentCount.toString().padStart(2, '0')}]</span>
                    </button>
                </div>

                {/* Navigation Links */}
                <nav className="flex-1 px-4 space-y-2 overflow-y-auto no-scrollbar">
                    {navItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => {
                                onViewChange(item.view);
                                if (item.tab) setDashboardTab(item.tab);
                            }}
                            className={`w-full flex items-center justify-between px-4 py-4 rounded-2xl text-sm font-bold transition-all group ${item.active
                                ? 'bg-teal text-white shadow-md'
                                : 'text-espresso/60 hover:text-espresso hover:bg-stone/50'
                                }`}
                        >
                            <div className="flex items-center gap-4">
                                <span className={`text-lg transition-transform group-hover:scale-110 ${item.active ? 'grayscale-0' : 'grayscale opacity-50'}`}>
                                    {item.icon}
                                </span>
                                <span>{item.label}</span>
                            </div>
                            {item.badge && (
                                <span className={`px-2 py-0.5 rounded-full text-[0.625rem] ${item.active ? 'bg-white text-teal' : 'bg-teal text-white'}`}>
                                    {item.badge}
                                </span>
                            )}
                        </button>
                    ))}
                </nav>

                {/* Sidebar Footer */}
                <div className="p-6 border-t border-espresso/5 flex flex-col gap-3">
                    <button
                        onClick={() => window.location.hash = 'home'}
                        className="w-full bg-stone text-espresso/60 border border-espresso/5 hover:text-espresso py-3 rounded-2xl text-[0.625rem] font-black uppercase tracking-widest transition-all"
                    >
                        Return to Main Site
                    </button>
                    <button
                        onClick={handleSignOut}
                        className="w-full bg-fail/5 text-fail border border-fail/10 hover:bg-fail/10 py-3 rounded-2xl text-[0.625rem] font-black uppercase tracking-widest transition-all"
                    >
                        Sign Out
                    </button>
                    <p className="text-[0.5rem] text-center text-espresso/40 uppercase tracking-widest font-black py-2">
                        Accessibility Scout v4.1.0
                    </p>
                </div>
            </aside>

            {/* Main Content Area */}
            <main 
                ref={scrollContainerRef}
                className="flex-1 h-screen overflow-y-auto relative no-scrollbar"
            >
                {visibleAnnouncements.length > 0 && (
                    <div className="px-6 pt-5 space-y-3 print:hidden">
                        {visibleAnnouncements.map(announcement => {
                            const tone = announcement.severity === 'critical'
                                ? 'border-(--error) bg-(--error)/10 text-(--error)'
                                : announcement.severity === 'warning'
                                    ? 'border-amber-500 bg-amber-500/10 text-amber-700'
                                    : 'border-teal bg-teal/10 text-teal';
                            return (
                                <div key={announcement.id} className={`flex items-start justify-between gap-4 rounded-2xl border-l-4 px-5 py-4 ${tone}`} role="status">
                                    <div><p className="text-[0.625rem] font-black uppercase tracking-[0.2em]">{announcement.title}</p><p className="mt-1 text-xs font-semibold leading-relaxed">{announcement.message}</p></div>
                                    <button onClick={() => dismissAnnouncement(announcement.id)} className="shrink-0 text-xs font-black opacity-60 hover:opacity-100" aria-label="Dismiss announcement">✕</button>
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className="relative z-10 w-full min-h-full">
                    {children}
                </div>
            </main>
        </div>
    );
}
